import { STRING_FILTER_OPERATORS } from 'src/contstant/FilterTypes';
import { FilterItemType } from 'src/hooks/useGenericFilter';
import { Location } from '@amzn/gbsa-helios-typescript-client';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { createDateFilterConfig } from 'src/components/filter/FilterDatePicker';
import { formatDateTime } from 'src/utils/generalUtils';
import {
  TableProps,
  PropertyFilterProps,
  StatusIndicator,
} from '@cloudscape-design/components';
import { COUNTRY_OPTIONS, REGION_OPTIONS } from 'src/contstant/commonLabels';
import { BusinessUnitDetails } from '@amzn/gbsa-orbit-typescript-client';

export enum LocationPropertyKey {
  BUSINESS_UNIT = 'parent.businessUnit',
  ACTIVE_STATUS = 'parent.activeStatus',
  LAST_UPDATED_AT = 'parent.LastUpdatedAt',
  CREATED_AT = 'parent.createdAt',
  REGION = 'region',
  COUNTRY = 'country',
}

// Initial filter options
export const initialFilters = [
  {
    propertyKey: LocationPropertyKey.ACTIVE_STATUS,
    value: 'true',
    label: 'Active',
  },
  {
    propertyKey: LocationPropertyKey.ACTIVE_STATUS,
    value: 'false',
    label: 'Inactive',
  },
];
export const getItemValue = (item: Location, propertyKey: string): any => {
  if (!item) return '';

  switch (propertyKey) {
    case LocationPropertyKey.BUSINESS_UNIT:
      return {
        itemValue: item.businessUnitId,
        type: FilterItemType.TEXT,
      };
    case LocationPropertyKey.REGION:
      return {
        itemValue: item.region,
        type: FilterItemType.TEXT,
      };
    case LocationPropertyKey.COUNTRY:
      return {
        itemValue: item.country,
        type: FilterItemType.TEXT,
      };
    case LocationPropertyKey.ACTIVE_STATUS:
      return {
        itemValue: item.isEnabledForBU,
        type: FilterItemType.BOOLEAN,
      };
    case LocationPropertyKey.LAST_UPDATED_AT:
      return {
        itemValue: item.lastUpdatedAt,
        type: FilterItemType.DATE,
      };
    case LocationPropertyKey.CREATED_AT:
      return {
        itemValue: item.createdAt,
        type: FilterItemType.DATE,
      };
    default:
      return { itemValue: '' };
  }
};

export const getSearchableFields = (item: Location) => [
  item.locationCode || '',
  item.state || '',
  item.country || '',
  item.region || '',
];

export const getFilterLocationsProperties = (isPlatformAdmin: boolean): any[] => {
  const baseFilters: (PropertyFilterProps.FilteringProperty & {
    key: keyof Location;
  })[] = [
    {
      key: 'locationCode',
      propertyLabel: 'Location Code',
      operators: STRING_FILTER_OPERATORS,
      groupValuesLabel: 'Location Code values',
    },
    {
      key: 'region',
      propertyLabel: 'Region',
      operators: STRING_FILTER_OPERATORS,
      groupValuesLabel: 'Region values',
    },
    {
      key: 'country',
      propertyLabel: 'Country',
      operators: STRING_FILTER_OPERATORS,
      groupValuesLabel: 'Country values',
    },
    {
      key: 'isEnabledForBU',
      propertyLabel: 'Status',
      operators: STRING_FILTER_OPERATORS,
      groupValuesLabel: 'Status values',
    },
    createDateFilterConfig({
      key: 'createdAt',
      propertyLabel: 'Created At',
      groupValuesLabel: 'Date',
    }) as any,
    createDateFilterConfig({
      key: 'lastUpdatedAt',
      propertyLabel: 'Updated At',
      groupValuesLabel: 'Date',
    }) as any,
  ];
  if (isPlatformAdmin) {
    baseFilters.push({
      key: 'businessUnitId',
      propertyLabel: 'Business Unit',
      operators: STRING_FILTER_OPERATORS,
      groupValuesLabel: 'Business Unit values',
    });
  }
  return baseFilters;
};
// Table column definitions for the locations table
export const getLocationsTableDefinitions = (
  businessUnitList: BusinessUnitDetails[] = [],
): TableProps.ColumnDefinition<Location>[] => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return [
    {
      id: 'locations',
      header: 'Location',
      sortingField: 'locationCode',
      cell: (item) => item.locationCode,
      isRowHeader: true,
    },
    {
      id: 'isEnabledForBU',
      header: 'Status',
      sortingField: 'isEnabledForBU',
      cell: (item) => {
        const status = item.isEnabledForBU;
        return (
          <StatusIndicator type={status ? 'success' : 'error'}>
            {status ? 'Active' : 'Inactive'}
          </StatusIndicator>
        );
      },
    },
    {
      id: 'businessUnit',
      header: 'Business Unit',
      sortingField: 'businessUnitId',
      cell: (item) => getBusinessUnitName(item.businessUnitId),
    },
    {
      id: 'region',
      header: 'Region',
      sortingField: 'region',
      cell: (item) => item.region || '',
    },
    {
      id: 'country',
      header: 'Country',
      sortingField: 'country',
      cell: (item) => item.country || '',
    },

    {
      id: 'createdAt',
      header: 'Created at',
      sortingField: 'createdAt',
      cell: (item) => formatDateTime(item?.createdAt as any) || '-',
    },
    {
      id: 'createdBy',
      header: 'Created by',
      sortingField: 'createdBy',
      cell: (item) => item.createdBy || '',
    },
    {
      id: 'lastUpdatedAt',
      header: 'Last Updated at',
      sortingField: 'lastUpdatedAt',
      cell: (item) => formatDateTime(item?.lastUpdatedAt as any) || '-',
    },
    {
      id: 'lastUpdatedBy',
      header: 'Last Updated by',
      sortingField: 'lastUpdatedBy',
      cell: (item) => item.lastUpdatedBy || '',
    },
  ];
};

export const defaultContentDisplay = [
  { id: 'locations', visible: true },
  { id: 'isEnabledForBU', visible: true },
  { id: 'businessUnit', visible: true },
  { id: 'region', visible: true },
  { id: 'country', visible: true },
  { id: 'createdAt', visible: false },
  { id: 'createdBy', visible: false },
  { id: 'lastUpdatedAt', visible: false },
  { id: 'lastUpdatedBy', visible: false },
];

export const contentDisplayPreference = {
  title: 'Column preferences',
  description: 'Customize which columns are shown',
  options: [
    { id: 'locations', label: 'Locations' },
    { id: 'isEnabledForBU', label: 'Status' },
    { id: 'businessUnit', label: 'Business Unit' },
    { id: 'region', label: 'Region' },
    { id: 'country', label: 'Country' },
    { id: 'createdAt', label: 'Created At' },
    { id: 'createdBy', label: 'Created By' },
    { id: 'lastUpdatedAt', label: 'Last Updated At' },
    { id: 'lastUpdatedBy', label: 'Last Updated By' },
  ],
};

export const pageSizePreference = {
  title: 'Page size',
  options: [
    { value: 10, label: '10 items' },
    { value: 20, label: '20 items' },
    { value: 30, label: '30 items' },
    { value: 50, label: '50 items' },
    { value: 100, label: '100 items' },
  ],
};

// Get predefined filter options for locations
export const getLocationFilterOptions = () => {
  const countryOptions = COUNTRY_OPTIONS.map((option) => ({
    propertyKey: LocationPropertyKey.COUNTRY,
    value: option.value,
    label: option.label,
  }));

  const regionOptions = REGION_OPTIONS.map((option) => ({
    propertyKey: LocationPropertyKey.REGION,
    value: option.value,
    label: option.label,
  }));

  return [...countryOptions, ...regionOptions, ...initialFilters];
};

export const getExportLocationConfig = (
  businessUnitList: BusinessUnitDetails[] = [],
): ExportFileConfig => {
  const getBusinessUnitName = (businessUnitId: string) => {
    const businessUnit = businessUnitList.find(
      (bu) => bu.businessUnitId === businessUnitId,
    );
    return businessUnit ? businessUnit.name : businessUnitId;
  };

  return {
    fileName: 'location_list.csv',
    mappings: {
      'Location': (location: Location) => location.locationCode,
      'Business Unit': (location: Location) =>
        getBusinessUnitName(location.businessUnitId),
      'Region': (location: Location) => location.region,
      'Country': (location: Location) => location.country,
      'Status': (location: Location) => (location.isEnabledForBU ? 'Active' : 'Inactive'),
      'Created By': (location: Location) => location.createdBy || '-',
      'Created At': (location: Location) =>
        formatDateTime(location?.createdAt as any) || '-',
      'Last Updated By': (location: Location) => location.lastUpdatedBy || '-',
      'Last Updated At': (location: Location) =>
        formatDateTime(location?.lastUpdatedAt as any) || '-',
    },
  };
};
